import { Select } from '@thanx/uikit/select'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { PosProvider, POS_PROVIDERS } from 'api/captureRate'
import Locations from 'components/LocationSelector'
import Spinner from 'components/Spinner'
import { LocationContext } from 'contexts/LocationContext'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import { useContext, useEffect, useMemo, useState } from 'react'
import Table from './Table'
import { useRevenueSources } from './useRevenueSources'

const t = buildTranslate('capture_rate.revenue_sources')

const isKnownPosProvider = (value: string): value is PosProvider =>
  (POS_PROVIDERS as readonly string[]).includes(value)

// Maps a location's specific integration variant to the base provider pos_orders uses.
const POS_PROVIDER_ALIASES: Record<string, PosProvider> = {
  brink_direct: 'brink',
  ncr_aloha: 'ncr',
  square_v2: 'square',
}

const toPosProvider = (value: string): PosProvider | undefined =>
  isKnownPosProvider(value) ? value : POS_PROVIDER_ALIASES[value]

const RevenueSources = () => {
  const [css] = useStyletron()
  const merchant = useCurrentMerchant()
  const { currentLocation } = useContext(LocationContext)
  const [posFilter, setPosFilter] = useState<PosProvider | 'all'>('all')

  // Pos::OrderMapping (the channel table's data) is merchant-scoped, not
  // location-scoped, so the available providers -- and thus the filter's
  // options -- must stay merchant-wide regardless of which location is
  // selected. Location only narrows which revenue numbers load (below).
  const availablePosProviders = useMemo(() => {
    const values = merchant?.all_redemption_providers.map(p => p.value) ?? []

    return Array.from(
      new Set(
        values
          .filter((v): v is string => !!v)
          .map(toPosProvider)
          .filter((v): v is PosProvider => !!v)
      )
    )
  }, [merchant])

  const showPosFilter =
    !!merchant?.has_multi_pos && availablePosProviders.length > 0

  // Selecting a location the current provider isn't on would otherwise
  // silently filter every row out.
  useEffect(() => {
    if (posFilter !== 'all' && !availablePosProviders.includes(posFilter)) {
      setPosFilter('all')
    }
  }, [availablePosProviders, posFilter])

  const { rows, isLoading, error } = useRevenueSources(
    currentLocation?.id,
    posFilter
  )

  const posOptions = useMemo(
    () => [
      { value: 'all', label: t('pos_system_all') },
      ...availablePosProviders.map(provider => ({
        value: provider,
        label: t(`table.provider.${provider}`),
      })),
    ],
    [availablePosProviders]
  )
  const selectedPosOption =
    posOptions.find(option => option.value === posFilter) ?? posOptions[0]

  return (
    <div>
      <Text.Header4 bold>{t('title')}</Text.Header4>
      <Text.BodyText4 color="grey70">{t('subtitle')}</Text.BodyText4>

      <div
        className={css({
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          marginTop: '24px',
          marginBottom: '32px',
        })}
      >
        {showPosFilter && (
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '260px',
              flex: '0 0 auto',
            })}
          >
            <Text.BodyText5
              bold
              color="grey70"
              className={css({
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              })}
            >
              {t('pos_system_label')}
            </Text.BodyText5>
            <Select
              aria-label={t('table.pos')}
              value={selectedPosOption}
              options={posOptions}
              searchable={false}
              clearable={false}
              onChange={selected => {
                const value = selected[0]?.value ?? 'all'
                setPosFilter(value as PosProvider | 'all')
              }}
            />
          </div>
        )}

        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '500px',
            flex: '0 0 auto',
          })}
        >
          <Text.BodyText5
            bold
            color="grey70"
            className={css({
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            })}
          >
            {t('location_label')}
          </Text.BodyText5>
          <Locations compact />
        </div>
      </div>

      <Spinner isLoading={isLoading}>
        {error && (
          <div aria-live="polite" aria-atomic="true">
            <Text.BodyText4 color="negative">{t('error')}</Text.BodyText4>
          </div>
        )}

        {!error && rows.length === 0 && (
          <Text.BodyText4 color="grey70">{t('empty')}</Text.BodyText4>
        )}

        {!error && rows.length > 0 && <Table rows={rows} />}
      </Spinner>
    </div>
  )
}

export default RevenueSources
